import { Info } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import { Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip';
import type { ChartDatum } from '../types/gmfDeveloperPipeline.types';

const chartColors = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--primary-light)'];

interface ChartCardProps {
  title: string;
  data: ChartDatum[];
  tooltip: string;
}

const ChartInfo = ({ title, tooltip }: { title: string; tooltip: string }) => (
  <TooltipProvider>
    <ShadTooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          className='cursor-help rounded-sm text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          <Info className='size-4' aria-hidden='true' />
          <span className='sr-only'>{title} purpose</span>
        </button>
      </TooltipTrigger>
      <TooltipContent className='max-w-xs'>{tooltip}</TooltipContent>
    </ShadTooltip>
  </TooltipProvider>
);

const EmptyChart = () => (
  <div className='flex h-[260px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 px-6 text-center text-sm text-muted-foreground'>
    No submitted data yet.
  </div>
);

export const VerticalBarChartCard = ({ title, data, tooltip }: ChartCardProps) => (
  <Card className='h-full rounded-lg py-0'>
    <CardHeader className='flex-row items-center justify-between space-y-0 p-6'>
      <CardTitle className='text-base'>{title}</CardTitle>
      <ChartInfo title={title} tooltip={tooltip} />
    </CardHeader>
    <CardContent className='px-6 pb-6'>
      {data.length === 0 ? (
        <EmptyChart />
      ) : (
        <ResponsiveContainer width='100%' height={300}>
          <BarChart data={data.slice(0, 6)} layout='vertical' margin={{ left: 0, right: 20 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' opacity={0.45} />
            <XAxis type='number' tickLine={false} axisLine={false} className='text-xs' />
            <YAxis dataKey='name' type='category' width={78} tickLine={false} axisLine={false} className='text-xs' />
            <Tooltip
              cursor={{ fill: 'var(--muted)' }}
              contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8 }}
            />
            <Bar dataKey='value' radius={[0, 4, 4, 0]}>
              {data.slice(0, 6).map((entry, index) => (
                <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);

export const NeighborhoodUnitsChart = ({ title, data, tooltip }: ChartCardProps) => (
  <Card className='h-full rounded-lg py-0'>
    <CardHeader className='flex-row items-center justify-between space-y-0 p-6'>
      <CardTitle className='text-base'>{title}</CardTitle>
      <ChartInfo title={title} tooltip={tooltip} />
    </CardHeader>
    <CardContent className='px-6 pb-6'>
      {data.length === 0 ? (
        <EmptyChart />
      ) : (
        <ResponsiveContainer width='100%' height={300}>
          <BarChart data={data.slice(0, 5)} margin={{ left: 0, right: 16, bottom: 28 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' opacity={0.45} />
            <XAxis dataKey='name' tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor='end' className='text-xs' />
            <YAxis tickLine={false} axisLine={false} className='text-xs' />
            <Tooltip
              cursor={{ fill: 'var(--muted)' }}
              contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8 }}
            />
            <Bar dataKey='value' radius={[4, 4, 0, 0]}>
              {data.slice(0, 5).map((entry, index) => (
                <Cell key={entry.name} fill={chartColors[(index + 1) % chartColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);

export const DonutChartCard = ({ title, data, tooltip }: ChartCardProps) => (
  <Card className='h-full rounded-lg py-0'>
    <CardHeader className='flex-row items-center justify-between space-y-0 p-6'>
      <CardTitle className='text-base'>{title}</CardTitle>
      <ChartInfo title={title} tooltip={tooltip} />
    </CardHeader>
    <CardContent className='px-6 pb-6'>
      {data.length === 0 ? (
        <EmptyChart />
      ) : (
        <ResponsiveContainer width='100%' height={260}>
          <PieChart>
            <Pie data={data} cx='50%' cy='45%' innerRadius={56} outerRadius={82} paddingAngle={2} dataKey='value' nameKey='name'>
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8 }} />
            <Legend verticalAlign='bottom' height={40} iconType='circle' />
          </PieChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);

export const PipelineStageChartCard = ({ title, data, tooltip }: ChartCardProps) => (
  <Card className='h-full rounded-lg py-0'>
    <CardHeader className='flex-row items-center justify-between space-y-0 p-6'>
      <CardTitle className='text-base'>{title}</CardTitle>
      <ChartInfo title={title} tooltip={tooltip} />
    </CardHeader>
    <CardContent className='px-6 pb-6'>
      {data.length === 0 ? (
        <EmptyChart />
      ) : (
        <ResponsiveContainer width='100%' height={300}>
          <BarChart data={data} layout='vertical'>
            <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' opacity={0.45} />
            <XAxis type='number' tickLine={false} axisLine={false} className='text-xs' />
            <YAxis dataKey='name' type='category' width={124} tickLine={false} axisLine={false} className='text-xs' />
            <Tooltip
              cursor={{ fill: 'var(--muted)' }}
              contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8 }}
            />
            <Bar dataKey='value' radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={chartColors[(index + 2) % chartColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);
